import React from 'react';
import {
  Heart, Utensils, Dumbbell, Sparkles, Play, ChevronDown, ChevronUp,
  Calendar, Clock, Flame, Droplets, Moon, Sun, Leaf,
  AlertCircle, CheckCircle, Loader2, Youtube, ExternalLink,
  Download, FileText, Printer
} from 'lucide-react';

export const LifestylePlanResults = ({
  plan, expandedSections, toggleSection,
  handleExportPDF, handleExportWord, handlePrint,
  exporting, onNewPlan
}) => (
  <div className="space-y-6">
    {/* Plan Header */}
    <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-2xl font-bold">{plan.plan_name}</h2>
          <p className="text-emerald-100">Duration: {plan.duration} | Approach: {plan.approach}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExportPDF} disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 rounded-lg transition-colors disabled:opacity-50" data-testid="export-pdf-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} PDF
          </button>
          <button onClick={handleExportWord} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors" data-testid="export-word-btn">
            <FileText className="w-4 h-4" /> Word
          </button>
          <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors" data-testid="print-btn">
            <Printer className="w-4 h-4" /> Print
          </button>
          <button onClick={onNewPlan} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">New Plan</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        {[
          { icon: Flame, label: 'Daily Calories', value: plan.diet_plan.daily_calorie_target },
          { icon: Droplets, label: 'Hydration', value: `${plan.diet_plan.hydration_target_liters}L` },
          { icon: Dumbbell, label: 'Weekly Exercise', value: `${plan.workout_plan.total_weekly_minutes} min` },
          { icon: Leaf, label: 'Daily Yoga', value: `${plan.yoga_plan.total_duration_minutes} min` },
        ].map(s => (
          <div key={s.label} className="bg-slate-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 text-slate-400 mb-1"><s.icon className="w-4 h-4" /><span className="text-sm">{s.label}</span></div>
            <p className="text-2xl font-bold">{s.value}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Diet Plan */}
    <CollapsibleSection title="Diet Plan" subtitle={`${plan.diet_plan.daily_calorie_target} kcal/day \u2022 ${plan.diet_plan.meals.length} meals`}
      icon={Utensils} iconColor="orange" expanded={expandedSections.diet} onToggle={() => toggleSection('diet')}>
      <div className="mb-6">
        <h4 className="font-medium mb-3">Macro Distribution</h4>
        <div className="flex gap-4">
          {[
            { label: 'Carbs', value: plan.diet_plan.macro_distribution.carbs, color: 'blue' },
            { label: 'Protein', value: plan.diet_plan.macro_distribution.protein, color: 'red' },
            { label: 'Fats', value: plan.diet_plan.macro_distribution.fats, color: 'yellow' },
          ].map(m => (
            <div key={m.label} className={`flex-1 bg-${m.color}-500/20 rounded-lg p-3 text-center`}>
              <p className={`text-2xl font-bold text-${m.color}-400`}>{m.value}%</p>
              <p className="text-sm text-slate-400">{m.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <h4 className="font-medium mb-3">Daily Meals</h4>
        <div className="space-y-3">
          {plan.diet_plan.meals.map((meal, idx) => (
            <div key={`meal-${meal.meal_type}`} className="bg-slate-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span className="font-medium capitalize">{meal.meal_type.replace(/_/g, ' ')}</span>
                  <span className="text-slate-400">&bull; {meal.time}</span>
                </div>
                <span className="text-emerald-400 font-medium">{meal.calories} kcal</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {meal.foods.map((food, i) => <span key={`food-${food}`} className="px-2 py-1 bg-slate-600 rounded text-sm">{food}</span>)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium mb-2 flex items-center gap-2 text-emerald-400"><CheckCircle className="w-4 h-4" /> Foods to Include</h4>
          <ul className="space-y-1">{plan.diet_plan.foods_to_include.slice(0, 6).map((food) => <li key={food} className="text-sm text-slate-300">&bull; {food}</li>)}</ul>
        </div>
        <div>
          <h4 className="font-medium mb-2 flex items-center gap-2 text-red-400"><AlertCircle className="w-4 h-4" /> Foods to Avoid</h4>
          <ul className="space-y-1">{plan.diet_plan.foods_to_avoid.slice(0, 6).map((food) => <li key={food} className="text-sm text-slate-300">&bull; {food}</li>)}</ul>
        </div>
      </div>

      {plan.diet_plan.ayurvedic_recommendations && (
        <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <h4 className="font-medium mb-2 flex items-center gap-2 text-amber-400"><Leaf className="w-4 h-4" /> Ayurvedic Recommendations</h4>
          <ul className="space-y-1 text-sm text-slate-300">
            <li>&bull; {plan.diet_plan.ayurvedic_recommendations.warm_water}</li>
            <li>&bull; {plan.diet_plan.ayurvedic_recommendations.timing}</li>
            <li>&bull; Recommended spices: {plan.diet_plan.ayurvedic_recommendations.spices?.join(', ')}</li>
          </ul>
        </div>
      )}
    </CollapsibleSection>

    {/* Workout Plan */}
    <CollapsibleSection title="Workout Plan" subtitle={`${plan.workout_plan.total_weekly_minutes} min/week \u2022 ${plan.workout_plan.exercises.length} exercises`}
      icon={Dumbbell} iconColor="blue" expanded={expandedSections.workout} onToggle={() => toggleSection('workout')}>
      <div className="mb-6">
        <h4 className="font-medium mb-3">Weekly Schedule</h4>
        <div className="grid grid-cols-7 gap-2">
          {Object.entries(plan.workout_plan.weekly_schedule).map(([day, exercises]) => (
            <div key={day} className={`p-3 rounded-lg text-center ${plan.workout_plan.rest_days.includes(day) ? 'bg-slate-700/30' : 'bg-blue-500/10 border border-blue-500/30'}`}>
              <p className="text-xs font-medium mb-1">{day.slice(0, 3)}</p>
              <p className="text-xs text-slate-400">{plan.workout_plan.rest_days.includes(day) ? 'Rest' : `${exercises.length} ex`}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        {plan.workout_plan.exercises.map((exercise) => (
          <div key={exercise.name} className="bg-slate-700/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h5 className="font-medium">{exercise.name}</h5>
              <span className={`px-2 py-1 rounded text-xs ${
                exercise.intensity === 'high' ? 'bg-red-500/20 text-red-400' :
                exercise.intensity === 'moderate' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-green-500/20 text-green-400'
              }`}>{exercise.intensity}</span>
            </div>
            <p className="text-sm text-slate-400 mb-2">{exercise.instructions}</p>
            <div className="flex gap-4 text-xs text-slate-400">
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {exercise.duration_minutes} min</span>
              {exercise.calories_burned && <span className="flex items-center gap-1"><Flame className="w-3 h-3" /> {exercise.calories_burned} kcal</span>}
              <span className="capitalize">{exercise.category}</span>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-sm text-slate-400 italic">{plan.workout_plan.progression_notes}</p>
    </CollapsibleSection>

    {/* Yoga Plan */}
    <CollapsibleSection title="Yoga & Meditation" subtitle={`${plan.yoga_plan.total_duration_minutes} min/day \u2022 ${plan.yoga_plan.asanas.length} asanas`}
      icon={Leaf} iconColor="purple" expanded={expandedSections.yoga} onToggle={() => toggleSection('yoga')}>
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <h4 className="font-medium flex items-center gap-2 text-amber-400 mb-2"><Sun className="w-4 h-4" /> Morning Routine</h4>
          <ul className="space-y-1 text-sm">{plan.yoga_plan.daily_routine.morning?.map((item) => <li key={item}>&bull; {item}</li>)}</ul>
        </div>
        <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
          <h4 className="font-medium flex items-center gap-2 text-indigo-400 mb-2"><Moon className="w-4 h-4" /> Evening Routine</h4>
          <ul className="space-y-1 text-sm">{plan.yoga_plan.daily_routine.evening?.map((item) => <li key={item}>&bull; {item}</li>)}</ul>
        </div>
      </div>

      <h4 className="font-medium mb-3">Recommended Asanas</h4>
      <div className="space-y-4">
        {plan.yoga_plan.asanas.map((asana) => (
          <div key={asana.name} className="bg-slate-700/50 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div><h5 className="font-medium text-lg">{asana.name}</h5><p className="text-purple-400 text-sm italic">{asana.sanskrit_name}</p></div>
              <span className={`px-2 py-1 rounded text-xs ${asana.difficulty === 'beginner' ? 'bg-green-500/20 text-green-400' : asana.difficulty === 'intermediate' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>{asana.difficulty}</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">{asana.benefits.map((b) => <span key={b} className="px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded text-xs">{b}</span>)}</div>
            <div className="flex gap-4 text-sm text-slate-400 mb-3">
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {asana.duration_minutes} min</span>
              {asana.repetitions && <span>{asana.repetitions} repetitions</span>}
            </div>
            {asana.contraindications?.length > 0 && (
              <div className="mb-3 p-2 bg-red-500/10 rounded text-xs text-red-400"><AlertCircle className="w-3 h-3 inline mr-1" />Avoid if: {asana.contraindications.join(', ')}</div>
            )}
            {asana.youtube_videos?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-600">
                <p className="text-sm text-slate-400 mb-2 flex items-center gap-1"><Youtube className="w-4 h-4 text-red-500" /> Video Demonstrations</p>
                <div className="flex flex-wrap gap-2">
                  {asana.youtube_videos.map((video) => (
                    <a key={video.url} href={video.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg transition-colors">
                      <Play className="w-4 h-4 text-red-500" /><span className="text-sm truncate max-w-[200px]">{video.title}</span><ExternalLink className="w-3 h-3 text-slate-400" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {plan.yoga_plan.pranayama?.length > 0 && (
        <div className="mt-6">
          <h4 className="font-medium mb-3">Pranayama (Breathing Exercises)</h4>
          <div className="grid md:grid-cols-2 gap-3">
            {plan.yoga_plan.pranayama.map((p) => (
              <div key={p.name} className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-4">
                <h5 className="font-medium text-teal-400">{p.name}</h5>
                <p className="text-sm text-slate-400 mt-1">{p.technique}</p>
                <p className="text-xs text-slate-500 mt-2">{p.duration_minutes} minutes</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {plan.yoga_plan.meditation?.length > 0 && (
        <div className="mt-6">
          <h4 className="font-medium mb-3">Meditation Practices</h4>
          <div className="grid md:grid-cols-2 gap-3">
            {plan.yoga_plan.meditation.map((m) => (
              <div key={m.name} className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
                <h5 className="font-medium text-indigo-400">{m.name}</h5>
                <p className="text-sm text-slate-400 mt-1">{m.technique}</p>
                <p className="text-xs text-slate-500 mt-2">{m.duration_minutes} minutes</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </CollapsibleSection>

    {/* Recommendations */}
    <div className="bg-gradient-to-r from-emerald-600/10 to-teal-600/10 border border-emerald-500/30 rounded-2xl p-6">
      <h3 className="text-xl font-semibold mb-4 flex items-center gap-2"><Heart className="w-5 h-5 text-emerald-500" /> Daily Recommendations</h3>
      <ul className="grid md:grid-cols-2 gap-3">
        {plan.recommendations.map((rec) => (
          <li key={rec} className="flex items-start gap-2 text-sm"><CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /><span>{rec}</span></li>
        ))}
      </ul>
    </div>

    {/* Progress Milestones */}
    <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-6">
      <h3 className="text-xl font-semibold mb-4 flex items-center gap-2"><Calendar className="w-5 h-5 text-purple-500" /> Progress Milestones</h3>
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-700"></div>
        <div className="space-y-4">
          {plan.progress_milestones.map((milestone) => (
            <div key={`week-${milestone.week}`} className="relative flex items-start gap-4 pl-8">
              <div className="absolute left-2 w-4 h-4 bg-purple-500 rounded-full border-4 border-slate-900"></div>
              <div className="flex-1 bg-slate-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium">Week {milestone.week}</span>
                  <span className="text-xs text-purple-400">{milestone.metric}</span>
                </div>
                <p className="text-sm text-slate-400">{milestone.goal}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* Reusable collapsible section wrapper */
const CollapsibleSection = ({ title, subtitle, icon: Icon, iconColor, expanded, onToggle, children }) => (
  <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl overflow-hidden">
    <button onClick={onToggle} className="w-full p-6 flex items-center justify-between hover:bg-slate-700/30 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`p-2 bg-${iconColor}-500/20 rounded-lg`}><Icon className={`w-6 h-6 text-${iconColor}-500`} /></div>
        <div className="text-left"><h3 className="text-xl font-semibold">{title}</h3><p className="text-slate-400 text-sm">{subtitle}</p></div>
      </div>
      {expanded ? <ChevronUp /> : <ChevronDown />}
    </button>
    {expanded && <div className="p-6 pt-0 border-t border-slate-700">{children}</div>}
  </div>
);
